import React, { useState, createContext, useContext } from 'react';
type Language = 'es' | 'en';
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  currency: string;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
const translations = {
  es: {
    // Header
    'nav.sistema': 'El Sistema 24K',
    'nav.planes': 'Planes',
    'nav.testimonios': 'Casos de Éxito',
    'nav.quienes': 'Quiénes Somos',
    'nav.faq': 'FAQ',
    'nav.contact': 'Hablemos',
    // Hero
    'hero.badge': 'SISTEMA DE CONSTRUCCIÓN ACELERADA',
    'hero.title1': 'Tu Negocio Merece una',
    'hero.title2': 'Web Profesional.',
    'hero.title3': 'Y la Tienes en Menos de 48 Horas.',
    'hero.subtitle': 'Deja de perder clientes por no tener presencia digital. Nosotros construimos tu web hoy. Tú vendes desde mañana.',
    'hero.price.special': 'Precio especial de lanzamiento',
    'hero.cta': 'QUIERO MI WEB EN 48 HORAS',
    'hero.guarantee': 'Garantía de entrega',
    'hero.nohidden': 'Sin pagos ocultos',
    'hero.delivery': 'Entrega en menos de 48h',
    // Social Proof
    'social.clients': 'Clientes atendidos',
    'social.delivery': 'Entrega garantizada',
    'social.rating': 'Calificación promedio',
    // Pain Section
    'pain.badge': 'EL PROBLEMA',
    'pain.title': '¿Te suena familiar alguna de estas situaciones?',
    'pain.quote1': 'Tengo un gran producto o servicio... pero cuando alguien me busca en Google, no aparezco.',
    'pain.quote2': 'Me dijeron que una web tardaba 3 semanas y costaba $2,000. Lo dejé para después.',
    'pain.quote3': 'Mando clientes a mi perfil de Instagram y muchos no me toman en serio.',
    // Sistema 24K
    'sistema.title': 'EL SISTEMA 24K DE KINETIC',
    'sistema.text1': 'La razón por la que las webs tradicionales tardan semanas es porque están diseñadas para facturar más horas, no para darte resultados rápido.',
    'sistema.text2': 'Nosotros desarrollamos un sistema de construcción acelerada que nos permite entregar webs profesionales, optimizadas y listas para convertir...',
    'sistema.guarantee': 'EN MENOS DE 48 HORAS. GARANTIZADO.',
    // Main Offer
    'offer.title': 'Tu Web Lista Para',
    'offer.title2': 'Vender.',
    'offer.subtitle': 'Todo lo que necesitas para tener una presencia online profesional, sin complicaciones técnicas ni esperas interminables.',
    'offer.heading': 'WEB PROFESIONAL EN 48 HORAS',
    'offer.savings': 'Precio de Lanzamiento — Ahorras',
    'offer.cta': 'QUIERO MI WEB AHORA —',
    'offer.urgency': 'Quedan solo 7 cupos a este precio',
    // Pricing
    'pricing.title': 'Tu web está lista.',
    'pricing.title2': 'Ahora mantenla creciendo.',
    'pricing.subtitle': 'Planes de mantenimiento y crecimiento mensual para que tu negocio nunca deje de captar clientes.',
    'pricing.badge': 'MÁS VENDIDO',
    // About
    'about.badge': 'QUIÉNES SOMOS',
    'about.title': 'El Equipo Detrás de',
    'about.subtitle': 'Dos expertas unidas por una misión: transformar negocios con presencia digital de alto impacto.',
    // Testimonials
    'testimonials.title': 'LO QUE DICEN NUESTROS CLIENTES',
    'testimonials.subtitle': '"Resultados reales. Personas reales."',
    // Guarantee
    'guarantee.title': 'GARANTÍA KINETIC',
    'guarantee.text1': 'Si no entregamos tu web en',
    'guarantee.text2': '48 horas',
    'guarantee.text3': '...te devolvemos el',
    'guarantee.text4': '100% de tu dinero.',
    'guarantee.text5': 'Sin preguntas. Sin burocracia. Sin drama.',
    'guarantee.text6': 'Porque si nosotros no cumplimos, tú no pagas.',
    // FAQ
    'faq.title': 'Preguntas Frecuentes',
    'faq.subtitle': 'Resolvemos tus dudas para que des el paso hoy mismo.',
    // Final CTA
    'cta.title': 'Cada día sin web es un cliente que se va a la competencia.',
    'cta.text1': 'Hoy puedes cambiarlo.',
    'cta.text2': 'Por',
    'cta.text3': '. En menos de 48 horas.',
    'cta.text4': 'Sin riesgo. Con garantía.',
    'cta.countdown': 'Quedan',
    'cta.countdown2': 'a este precio',
    'cta.button': 'SÍ, QUIERO MI WEB AHORA',
    'cta.prefer': '¿Prefieres hablar primero?',
    'cta.whatsapp': 'Escríbenos por WhatsApp',
    // Footer
    'footer.tagline': 'Transformando negocios con presencia digital de alto impacto en tiempo récord.',
    'footer.terms': 'Términos y Condiciones',
    'footer.privacy': 'Política de Privacidad',
    'footer.rights': 'Todos los derechos reservados.'
  },
  en: {
    // Header
    'nav.sistema': 'The 24K System',
    'nav.planes': 'Plans',
    'nav.testimonios': 'Success Stories',
    'nav.quienes': 'About Us',
    'nav.faq': 'FAQ',
    'nav.contact': "Let's Talk",
    // Hero
    'hero.badge': 'ACCELERATED CONSTRUCTION SYSTEM',
    'hero.title1': 'Your Business Deserves a',
    'hero.title2': 'Professional Website.',
    'hero.title3': 'And You Get It in Less Than 48 Hours.',
    'hero.subtitle': 'Stop losing customers for not having a digital presence. We build your website today. You sell from tomorrow.',
    'hero.price.special': 'Special launch price',
    'hero.cta': 'I WANT MY WEBSITE IN 48 HOURS',
    'hero.guarantee': 'Delivery guarantee',
    'hero.nohidden': 'No hidden fees',
    'hero.delivery': 'Delivery in less than 48h',
    // Social Proof
    'social.clients': 'Clients served',
    'social.delivery': 'Guaranteed delivery',
    'social.rating': 'Average rating',
    // Pain Section
    'pain.badge': 'THE PROBLEM',
    'pain.title': 'Does any of these situations sound familiar?',
    'pain.quote1': "I have a great product or service... but when someone searches for me on Google, I don't appear.",
    'pain.quote2': 'I was told a website would take 3 weeks and cost $2,000. I left it for later.',
    'pain.quote3': "I send clients to my Instagram profile and many don't take me seriously.",
    // Sistema 24K
    'sistema.title': 'THE KINETIC 24K SYSTEM',
    'sistema.text1': 'The reason traditional websites take weeks is because they are designed to bill more hours, not to give you results quickly.',
    'sistema.text2': 'We developed an accelerated construction system that allows us to deliver professional, optimized websites ready to convert...',
    'sistema.guarantee': 'IN LESS THAN 48 HOURS. GUARANTEED.',
    // Main Offer
    'offer.title': 'Your Website Ready to',
    'offer.title2': 'Sell.',
    'offer.subtitle': 'Everything you need to have a professional online presence, without technical complications or endless waits.',
    'offer.heading': 'PROFESSIONAL WEBSITE IN 48 HOURS',
    'offer.savings': 'Launch Price — You Save',
    'offer.cta': 'I WANT MY WEBSITE NOW —',
    'offer.urgency': 'Only 7 spots left at this price',
    // Pricing
    'pricing.title': 'Your website is ready.',
    'pricing.title2': 'Now keep it growing.',
    'pricing.subtitle': 'Monthly maintenance and growth plans so your business never stops capturing clients.',
    'pricing.badge': 'MOST POPULAR',
    // About
    'about.badge': 'ABOUT US',
    'about.title': 'The Team Behind',
    'about.subtitle': 'Two experts united by a mission: transform businesses with high-impact digital presence.',
    // Testimonials
    'testimonials.title': 'WHAT OUR CLIENTS SAY',
    'testimonials.subtitle': '"Real results. Real people."',
    // Guarantee
    'guarantee.title': 'KINETIC GUARANTEE',
    'guarantee.text1': "If we don't deliver your website in",
    'guarantee.text2': '48 hours',
    'guarantee.text3': '...we refund',
    'guarantee.text4': '100% of your money.',
    'guarantee.text5': 'No questions. No bureaucracy. No drama.',
    'guarantee.text6': "Because if we don't deliver, you don't pay.",
    // FAQ
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'We answer your questions so you can take the step today.',
    // Final CTA
    'cta.title': 'Every day without a website is a customer going to the competition.',
    'cta.text1': 'Today you can change it.',
    'cta.text2': 'For',
    'cta.text3': '. In less than 48 hours.',
    'cta.text4': 'No risk. With guarantee.',
    'cta.countdown': 'Only',
    'cta.countdown2': 'left at this price',
    'cta.button': 'YES, I WANT MY WEBSITE NOW',
    'cta.prefer': 'Prefer to talk first?',
    'cta.whatsapp': 'Message us on WhatsApp',
    // Footer
    'footer.tagline': 'Transforming businesses with high-impact digital presence in record time.',
    'footer.terms': 'Terms and Conditions',
    'footer.privacy': 'Privacy Policy',
    'footer.rights': 'All rights reserved.'
  }
};
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [language, setLanguage] = useState<Language>('es');
  const t = (key: string): string => {
    return translations[language][key] || key;
  };
  const currency = language === 'es' ? '€' : '$';
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t,
    currency
  }} data-id="element-444">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}